import { Injectable } from '@angular/core';
import { ApiCustomerUsageService } from '../api-services/customer-usage.api.service';
import { CustomerUsageLimit } from '../models/usage/CustomerUsageLimit';
import { ProjectsService } from './projects.service';
import { CustomerUsage } from '../models/usage/CustomerUsage';
import { MessagesService } from './messages.service';
import { AssetManagementService } from './asset-management.service';
import { ApiAssetOnAssetManagementPageService } from '../api-services/asset-on-management-page.api.service';
import { BaseAsset } from '../models/assets/BaseAsset';
import { ApiAssetService } from '../api-services/asset.api.service';
import { MediaAsset } from '../models/assets/MediaAsset';
import { CryptoService } from './crypto.service';
import { StorageService } from './storageService.service';

@Injectable({
    providedIn: 'root'
})
export class CustomerUsageService {

    public customerUsageLimit: CustomerUsageLimit = new CustomerUsageLimit();
    public customerUsage: CustomerUsage = new CustomerUsage();
    public assetsOnManagementPage: MediaAsset[];
    public lrsUsageChartData = [0, 0];
    public fileStorageUsageChartData = [0, 0, 0];
    public streamingUsageChartData = [0, 0];
    public userUsageChartData = [0, 0];

    constructor(
        public ApiCustomerUsageService: ApiCustomerUsageService,
        public ApiAssetOnManagePageService: ApiAssetOnAssetManagementPageService,
        public ApiAssetService: ApiAssetService,
        public messageService: MessagesService,
        public cryptoService: CryptoService,
        public storageService: StorageService
    ) {
        let customerId: number = Number(this.cryptoService.decryptData(this.storageService.getItem('customerId')));
        this.customerUsage.fileStorageUsage = 0;
        this.customerUsageLimit.fileStorage = 0;
        this.customerUsage.userUsage = 0;
        this.customerUsageLimit.maxUsers = 0;
        this.getCustomerUsage(customerId);
    }

    getCustomerUsage(customerId: number) {
        return new Promise<void>(async (resolve) => {
            if (customerId == 0) {
                resolve();
                return;
            }

            let customerUsageLimitTemp = await this.ApiCustomerUsageService.getCustomerUsageLimitByCustomerId(customerId).toPromise();
            let customerUsageTemp = await this.ApiCustomerUsageService.getCustomerUsageByCustomerId(customerId).toPromise();
            if (!customerUsageTemp) {
                let customerUsageToDb = new CustomerUsage();
                customerUsageToDb.customerId = customerId;
                customerUsageToDb.lrsUsage = 0;
                customerUsageToDb.fileStorageUsage = 0;
                customerUsageToDb.streamingUsage = 0;
                customerUsageToDb.userUsage = 0;
                customerUsageTemp = await this.ApiCustomerUsageService.addCustomerUsage(customerUsageToDb).toPromise();
            } else {
                customerUsageTemp = await this.ApiCustomerUsageService.recalculateCustomerUsageByCustomerId(customerId).toPromise();
            }
            this.customerUsageLimit = customerUsageLimitTemp;
            this.customerUsage = customerUsageTemp;
            resolve();
        });
    }

    async getCustomerFileUsage() {
        await new Promise<void>(async (resolve) => {
            let customerId: number = Number(this.cryptoService.decryptData(this.storageService.getItem('customerId')));

            if (customerId == 0) {
                resolve();
                return;
            }

            let customerUsageLimitTemp = await this.ApiCustomerUsageService.getCustomerUsageLimitByCustomerId(customerId).toPromise();
            let customerUsageTemp = await this.ApiCustomerUsageService.getCustomerUsageByCustomerId(customerId).toPromise();

            this.customerUsageLimit = customerUsageLimitTemp;
            this.customerUsage = customerUsageTemp;
            resolve();
        });
    }

    getAssetsOnManagementPage(): Promise<BaseAsset[]> {
        let ids = [];
        return new Promise<BaseAsset[]>(async (resolve) => {
            this.ApiAssetOnManagePageService.getAssetsOnAssetManagementPageWithCustomerId(+this.cryptoService.decryptData(this.storageService.getItem("customerId"))).subscribe(assetsOnManagementPages => {
                assetsOnManagementPages.forEach(assetsOnManagementPage => {
                    ids.push(assetsOnManagementPage.baseAssetId);
                });

                this.ApiAssetService.getBaseAssetsWithBaseAssetIds(ids).subscribe(baseAssets => {
                    this.assetsOnManagementPage = baseAssets as MediaAsset[];
                    resolve(baseAssets);
                });
            });
        });
    }

    reinitCustomerUsage() {
        return new Promise<void>(async (resolve) => {
            await this.messageService.presentLoadingWindow("Loading Usage...");
            let customerId: number = Number(this.cryptoService.decryptData(this.storageService.getItem('customerId')));            
            await this.getCustomerUsage(customerId);
            await this.getAssetsOnManagementPage();

            this.setChartData();
            this.messageService.loadingWindow.dismiss();

            resolve();
        });
    }

    reinitCustomerUsageWithoutLoadingWindow() {
        return new Promise<void>(async (resolve) => {
            let customerId: number = Number(this.cryptoService.decryptData(this.storageService.getItem('customerId')));
            await this.getCustomerUsage(customerId);
            await this.getAssetsOnManagementPage();

            this.setChartData();

            resolve();
        });
    }

    setChartData() {
        var mediaStorage: number = this.assetsOnManagementPage.map(x => x.fileSize).reduce((a, b) => a + b, 0);
        this.lrsUsageChartData = [this.customerUsage.lrsUsage, this.customerUsageLimit.maxLrsUsers - this.customerUsage.lrsUsage];
        this.fileStorageUsageChartData = [this.customerUsage.fileStorageUsage - mediaStorage, mediaStorage, this.customerUsageLimit.fileStorage - this.customerUsage.fileStorageUsage];
        this.streamingUsageChartData = [this.customerUsage.streamingUsage, this.customerUsageLimit.streamingUsage - this.customerUsage.streamingUsage];
        this.userUsageChartData = [this.customerUsage.userUsage, this.customerUsageLimit.maxUsers - this.customerUsage.userUsage];
    }

    getRemainingLRSCredits(): Promise<number> {
        return new Promise<number>(async (resolve) => {
            let customerId: number = Number(this.cryptoService.decryptData(this.storageService.getItem('customerId')));
            await this.getCustomerUsage(customerId);
            resolve(this.customerUsageLimit.maxLrsUsers - this.customerUsage.lrsUsage);
        });
    }

    getRemainingStreamingCredits(): Promise<number> {
        return new Promise<number>(async (resolve) => {
            let customerId: number = Number(this.cryptoService.decryptData(this.storageService.getItem('customerId')));
            await this.getCustomerUsage(customerId);
            resolve(this.customerUsageLimit.streamingUsage - this.customerUsage.streamingUsage);
        });
    }

    getRemainingFileStorageCredits(): Promise<number> {
        return new Promise<number>(async (resolve) => {
            let customerId: number = Number(this.cryptoService.decryptData(this.storageService.getItem('customerId')));
            await this.getCustomerUsage(customerId);
            resolve(this.customerUsageLimit.fileStorage - this.customerUsage.fileStorageUsage);
        });
    }

    getLRSCreditsUsagePercentage(): Promise<number> {
        return new Promise<number>(async (resolve) => {
            let lrsCredits = await this.getRemainingLRSCredits();
            resolve(this.calculateUsagePercentage('LRS'));
        });
    }

    getStreamingCreditsUsagePercentage(): Promise<number> {
        return new Promise<number>(async (resolve) => {
            let streamingCredits = await this.getRemainingLRSCredits();
            resolve(this.calculateUsagePercentage('Streaming'));
        });
    }

    getFileStorageCreditsUsagePercentage(): Promise<number> {
        return new Promise<number>(async (resolve) => {
            await this.getRemainingFileStorageCredits();
            resolve(this.calculateUsagePercentage('File Storage'));
        });
    }

    calculateUsagePercentage(type: string): number {
        let percentageToReturn: number = 0;
        switch (type) {
            case 'LRS': {
                percentageToReturn = (this.customerUsage.lrsUsage / (this.customerUsageLimit.maxLrsUsers > 0 ? this.customerUsageLimit.maxLrsUsers : 1)) * 100;
                break;
            }
            case 'Streaming': {
                percentageToReturn = (this.customerUsage.streamingUsage / (this.customerUsageLimit.streamingUsage > 0 ? this.customerUsageLimit.streamingUsage : 1)) * 100;
                break;
            }
            case 'File Storage': {
                percentageToReturn = (this.customerUsage.fileStorageUsage / (this.customerUsageLimit.fileStorage > 0 ? this.customerUsageLimit.fileStorage : 1)) * 100;
                break;
            }
        }

        return percentageToReturn;
    }

    getIsFileStorageExceeded(): boolean {
        return this.customerUsage.fileStorageUsage >= this.customerUsageLimit.fileStorage;
    }

    getIsUserCreditsExceeded(): boolean {
        return this.customerUsage.userUsage >= this.customerUsageLimit.maxUsers;
    }

    /* async reinitCustomerAssetUsage(){
        await this.assetManagementService.getAssetsOnManagementPage();
        var size = this.assetManagementService.assets.map(x=>x.fileSize).reduce((a,b) => a+b,0);
        this.customerUsage.mediaStorage = size;
    }

    async calculateProjectUsage() {
        await this.projectService.getProjectStreamingUsage().then(x => {
            var projectSize = this.projectService.projects.map(x => x.projectSize).reduce((a, b) => a + b, 0);
            var projectStreamingSize = this.projectService.projects.map(x => x.usageValue).reduce((a, b) => a + b, 0);
            var projectLaunchCount = this.projectService.projects.map(x=>x.launchCount).reduce((a,b)=>a+b,0);
            this.customerUsage.projectStorage = projectSize;
            this.customerUsage.projectStreaming = projectStreamingSize;
            this.customerUsage.learningRecordStoreUsers = projectLaunchCount;
            
            this.setChartData();
        });
    } */


}